import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { m, AnimatePresence } from 'framer-motion';
import { useScrollLock } from '@/hooks/ui/useScrollLock';
import { FADE_IN, SCALE_IN, ANIMATION_CONFIG } from '@/constants/animation';

interface NativeModalProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    /** Defaults to 10000 (standard overlay z-index) */
    zIndex?: number;
    /** Defaults to true */
    closeOnBackdropClick?: boolean;
    onExited?: () => void;
}

/**
 * NativeModal - 移动端防御基线封装 (Modal 版)
 * - 锁定 html+body 滚动
 * - 统一 z-index 层级
 * - 统一样式 (Backdrop blur, centered)
 * - 统一动画
 */
export const NativeModal: React.FC<NativeModalProps> = ({
    isOpen,
    onClose,
    children,
    zIndex = 2147483647, // Max Safe Integer for top-level modals
    closeOnBackdropClick = true,
    onExited
}) => {
    // 锁定滚动
    useScrollLock(isOpen);

    return (
        <Dialog.Root
            open={isOpen}
            onOpenChange={(open) => {
                if (!open) onClose();
            }}
        >
            <AnimatePresence onExitComplete={onExited}>
                {isOpen && (
                    <Dialog.Portal forceMount>
                        {/* Backdrop */}
                        <Dialog.Overlay asChild>
                            <m.div
                                className="fixed inset-0 bg-black/40 backdrop-blur-sm touch-none"
                                style={{ zIndex }}
                                variants={FADE_IN}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                                transition={{ duration: ANIMATION_CONFIG.duration.normal }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onTouchStart={(e) => e.stopPropagation()} // Prevent ghost touches
                            />
                        </Dialog.Overlay>

                        {/* Content Container */}
                        <Dialog.Content asChild onPointerDownOutside={(e) => {
                            if (!closeOnBackdropClick) {
                                e.preventDefault();
                            }
                        }}>
                            <m.div
                                className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-[360px] p-4 focus:outline-none"
                                style={{ zIndex: zIndex + 1 }}
                                variants={SCALE_IN}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                                transition={{
                                    type: 'spring',
                                    stiffness: 300,
                                    damping: 30
                                }}
                            >
                                {children}
                            </m.div>
                        </Dialog.Content>
                    </Dialog.Portal>
                )}
            </AnimatePresence>
        </Dialog.Root>
    );
};
